import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import ct from 'countries-and-timezones';
import { useTranslation } from 'src/arb-wrapper';
import { PUBLISHER_TIMEZONES } from 'src/components/campaigns/forms/publisherTimezones';
import type { FormRhfControllerProps } from './types';

export interface TimezoneSelectProps extends Pick<FormRhfControllerProps, 'control'> {
  /** Form field path for the template (e.g., productStrategies.0.benderTemplates.0) */
  name: string;
  /** Country code to derive timezone options from */
  countryCode: string;
}

export interface TimezoneOption {
  value: string;
  label: string;
}

interface EnrichedTimezone {
  name: string;
  abbr: string;
  offset: number;
}

interface BuildTimezoneOptionsConfig {
  /**
   * When true, collapse zones that share the same (abbreviation, current offset) into a single
   * option. Only the sports per-country dropdown wants this: it renders the abbreviation onto an
   * image, so New_York vs Detroit (both EST) is a meaningless distinction. The publisher path
   * leaves it false so every vetted IANA name gets its own option.
   */
  collapseByAbbr: boolean;
}

/**
 * Resolves an IANA name to its current short abbreviation and UTC offset, or `null` if the runtime
 * rejects the zone. Some legacy/deprecated zones (e.g. "Factory") throw a RangeError under `Intl`.
 */
function resolveTimezone (name: string, now: Date, utcMs: number): EnrichedTimezone | null {
  try {
    const abbr = new Intl.DateTimeFormat('en-US', { timeZone: name, timeZoneName: 'short' }).formatToParts(now)
      .find(p => p.type === 'timeZoneName')?.value ?? name;
    const localMs = new Date(now.toLocaleString('en-US', { timeZone: name })).getTime();
    const offset = (localMs - utcMs) / 3600000;
    return { name, abbr, offset };
  } catch {
    return null;
  }
}

/** Formats a fractional-hour UTC offset as `UTC±h` or `UTC±h:mm`. */
function formatUtcOffset (offset: number): string {
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return m > 0 ? `UTC${sign}${h}:${String(m).padStart(2, '0')}` : `UTC${sign}${h}`;
}

/**
 * Builds offset-sorted timezone options from a list of IANA timezone names.
 * The stored value is the canonical IANA name (e.g., "America/New_York") and the label is
 * `"<abbr> (<IANA name>, UTC±h[:mm])"`. Whether same-abbreviation zones collapse into one option
 * is controlled per caller via `config.collapseByAbbr`.
 */
function buildTimezoneOptions (names: readonly string[], config: BuildTimezoneOptionsConfig): TimezoneOption[] {
  if (!names.length) return [];

  const now = new Date();
  // Use 'en-US' for date string parsing to guarantee a format that new Date() can parse.
  // Using undefined (browser locale) can produce non-Latin digits or unusual separators
  // that cause Date parsing to return NaN in some locales.
  const utcMs = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();

  const enriched: EnrichedTimezone[] = [];
  for (const name of names) {
    const resolved = resolveTimezone(name, now, utcMs);
    if (resolved) enriched.push(resolved);
    // Unresolvable zones (legacy names some runtimes reject, e.g. "Factory") are skipped;
    // resolveTimezone's try/catch keeps the country path from throwing.
  }
  enriched.sort((a, b) => a.offset - b.offset);

  const seen = new Set<string>();
  const results: TimezoneOption[] = [];
  for (const tz of enriched) {
    if (config.collapseByAbbr) {
      // Dedup by (abbreviation, offset), not abbreviation alone: across the full IANA list many
      // distinct zones share a short abbreviation (e.g. "CST" is both UTC-6 America/Chicago and
      // UTC+8 Asia/Shanghai). Keying on abbr alone would silently drop the second — and since
      // Intl short names are runtime-dependent, which one survives would vary by engine.
      const key = `${tz.abbr}|${tz.offset}`;
      if (seen.has(key)) continue;
      seen.add(key);
    }
    results.push({ value: tz.name, label: `${tz.abbr} (${tz.name}, ${formatUtcOffset(tz.offset)})` });
  }
  return results;
}

/**
 * Computes deduplicated timezone options for a country, sorted by current UTC offset.
 * Collapses same-(abbreviation, offset) zones because the sports dropdown renders the abbreviation
 * onto an image, where distinct IANA names sharing an abbreviation are indistinguishable.
 */
export function getTimezoneOptionsForCountry (countryCode: string): TimezoneOption[] {
  const timezones = ct.getTimezonesForCountry(countryCode);
  return buildTimezoneOptions((timezones ?? []).map(tz => tz.name), { collapseByAbbr: true });
}

/**
 * Computes the publisher-config timezone options: one option per vetted IANA name in
 * `PUBLISHER_TIMEZONES`, with NO (abbreviation, offset) collapse, sorted by current UTC offset.
 * Used where the timezone is not tied to a specific country (e.g., publisher account configs), so
 * every platform-supported zone remains selectable even when several share a short abbreviation.
 *
 * Memoized at module level: building the list resolves 44 IANA zones, each instantiating an
 * `Intl.DateTimeFormat` and parsing locale strings, and it's called from a render path
 * (`PublisherAccountFields`). The offsets only shift on DST transitions (twice a year), so a
 * session-level cache is sufficient and avoids recomputing on every re-render.
 */
let cachedAllOptions: TimezoneOption[] | null = null;
export function getPublisherTimezoneOptions (): TimezoneOption[] {
  if (!cachedAllOptions) {
    cachedAllOptions = buildTimezoneOptions(PUBLISHER_TIMEZONES, { collapseByAbbr: false });
  }
  return cachedAllOptions;
}

/**
 * Timezone selection component for sports templates.
 * Renders a checkbox ("Override Timezone on Asset") that, when checked,
 * reveals a dropdown with deduplicated IANA timezones for the given country.
 * Stores the selected timezone at `config.overrideAssetTimezone`.
 */
export const TimezoneSelect: React.FC<TimezoneSelectProps> = ({ control, name, countryCode }) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const fieldPath = `${name}.config.overrideAssetTimezone`;
  const overrideValue = useWatch({ control, name: fieldPath }) as string | undefined;
  const [enabled, setEnabled] = React.useState(!!overrideValue);

  React.useEffect(() => {
    setEnabled(!!overrideValue);
  }, [overrideValue]);

  const timezoneOptions = useMemo(() => getTimezoneOptionsForCountry(countryCode), [countryCode]);

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      setValue(fieldPath, undefined, { shouldDirty: true });
    }
  };

  if (!timezoneOptions.length) return null;

  return (
    <Column data-testid="timezone-override" spacing="100">
      <Checkbox
        checked={enabled}
        data-testid="override-timezone-checkbox"
        onChange={handleToggle}
      >
        {t('form_override_timezone')}
      </Checkbox>
      {enabled && (
        <Column data-testid="config-timezone" spacing="100">
          <Text type="b200">{t('form_timezone')}</Text>
          <Select
            data-testid="timezone-select"
            placeholder={t('form_select_placeholder')}
            value={overrideValue ?? ''}
            onChange={(v) => { setValue(fieldPath, v, { shouldDirty: true }); }}
          >
            {timezoneOptions.map((tz) => (
              <SelectOption key={tz.value} label={tz.label} value={tz.value} />
            ))}
          </Select>
        </Column>
      )}
    </Column>
  );
};
